/**
 * "Draw on Path" for BoundedText.
 *
 * Positions each glyph of a text string along an SVG path (translation + rotation
 * to match the path tangent), reusing fabric.js's own path-sampling utilities
 * (the same ones fabric.Text uses internally for its native `path` feature) and
 * opentype.js glyph outlines (the same source BoundedText already uses for normal
 * horizontal rendering in OpentypeIText.js).
 */

/**
 * Rotates a glyph's path commands around its own horizontal center and moves it
 * to the given point on the path.
 * @param {Array} commands opentype.js path commands (from glyph.getPath())
 * @param {number} centerOffsetX x offset (in glyph-local space) to treat as the glyph's center
 * @param {number} angle rotation angle in radians (path tangent angle)
 * @param {number} tx target x position (point on path)
 * @param {number} ty target y position (point on path)
 */
function transformGlyphCommands(commands, centerOffsetX, angle, tx, ty) {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    const rotate = (x, y) => {
        const localX = x - centerOffsetX;
        return {
            x: localX * cos - y * sin + tx,
            y: localX * sin + y * cos + ty
        };
    };

    return commands.map((cmd) => {
        const out = { type: cmd.type };
        if ('x' in cmd) {
            const p = rotate(cmd.x, cmd.y);
            out.x = p.x;
            out.y = p.y;
        }
        if ('x1' in cmd) {
            const p1 = rotate(cmd.x1, cmd.y1);
            out.x1 = p1.x;
            out.y1 = p1.y;
        }
        if ('x2' in cmd) {
            const p2 = rotate(cmd.x2, cmd.y2);
            out.x2 = p2.x;
            out.y2 = p2.y;
        }
        return out;
    });
}

function addCommandsToCtx(ctx, commands) {
    for (let i = 0; i < commands.length; i++) {
        const cmd = commands[i];
        if (cmd.type === 'M') {
            ctx.moveTo(cmd.x, cmd.y);
        } else if (cmd.type === 'L') {
            ctx.lineTo(cmd.x, cmd.y);
        } else if (cmd.type === 'C') {
            ctx.bezierCurveTo(cmd.x1, cmd.y1, cmd.x2, cmd.y2, cmd.x, cmd.y);
        } else if (cmd.type === 'Q') {
            ctx.quadraticCurveTo(cmd.x1, cmd.y1, cmd.x, cmd.y);
        } else if (cmd.type === 'Z') {
            ctx.closePath();
        }
    }
}

/**
 * Expands a { minX, minY, maxX, maxY } bounding box (in place) to also cover every point in
 * `commands`. Used to know the actual ink extent of the rendered path text, needed to map a
 * gradient's normalized [0,1] space onto it (same idea as OpentypeIText._updateGradientFill,
 * which does this against the object's own width/height instead since normal text is rectangular).
 * @param {Array} commands
 * @param {{minX: number, minY: number, maxX: number, maxY: number}} bbox
 */
function expandBBoxWithCommands(commands, bbox) {
    for (let i = 0; i < commands.length; i++) {
        const cmd = commands[i];
        if ('x' in cmd) {
            bbox.minX = Math.min(bbox.minX, cmd.x);
            bbox.maxX = Math.max(bbox.maxX, cmd.x);
            bbox.minY = Math.min(bbox.minY, cmd.y);
            bbox.maxY = Math.max(bbox.maxY, cmd.y);
        }
    }
}

/**
 * Applies a `{ color, blur, offsetX, offsetY }` shadow description to the canvas 2D shadow
 * properties, mirroring `applyShadowFromContext()` in utils.js (which reads the same shape off
 * `ctx._fillShadow`). Text-on-path builds its own path per line/glyph instead of going through
 * drawPaths(), so the shadow description is threaded through as a plain option instead.
 * @param {CanvasRenderingContext2D} ctx
 * @param {{color?: string, blur?: number, offsetX?: number, offsetY?: number}} shadow
 */
function applyShadowToCtx(ctx, shadow) {
    ctx.shadowColor = shadow.color || '';
    ctx.shadowBlur = shadow.blur || 0;
    ctx.shadowOffsetX = shadow.offsetX || 0;
    ctx.shadowOffsetY = shadow.offsetY || 0;
}

function isFabricGradient(fill) {
    return !!fill && typeof fill === 'object' && fill instanceof fabric.Gradient;
}

function isMultiColorPalette(fill) {
    return (
        !!fill && typeof fill === 'object' && fill instanceof fabric.Palette && fill.colors && fill.colors.length > 1
    );
}

/**
 * Scales and centers an absolute, simplified path (M/L/C/Q/Z only) so its own bounding box
 * fits inside a `targetWidth` x `targetHeight` box centered at (0,0) - the same local coordinate
 * space item(0) (the OpentypeIText) already renders in. This decouples `textPath` from the
 * specific coordinates it was authored with, so the same path definition scales along with the
 * BoundedText's own width/height instead of being a fixed absolute size.
 * Aspect ratio is preserved (the path is scaled uniformly, not stretched).
 * @param {Array} parsedPath absolute, simplified path commands
 * @param {number} targetWidth
 * @param {number} targetHeight
 * @returns {Array} a new path command array, scaled and translated
 */
function normalizePathToBounds(parsedPath, targetWidth, targetHeight) {
    let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

    const visitPoint = (x, y) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
    };

    for (let i = 0; i < parsedPath.length; i++) {
        const cmd = parsedPath[i];
        // Every M/L/C/Q command ends in an (x, y) pair as its last two args; C/Q also have
        //  control points before it, which is fine to include too (a slightly looser but
        //  cheap-to-compute bounding box is good enough for normalization purposes)
        for (let j = 1; j < cmd.length - 1; j += 2) {
            visitPoint(cmd[j], cmd[j + 1]);
        }
    }

    if (!Number.isFinite(minX)) {
        return parsedPath;
    }

    const bboxWidth = maxX - minX || 1;
    const bboxHeight = maxY - minY || 1;
    const scale = Math.min(targetWidth / bboxWidth, targetHeight / bboxHeight);
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    const mapCoord = (x, y) => [(x - centerX) * scale, (y - centerY) * scale];

    return parsedPath.map((cmd) => {
        const out = [cmd[0]];
        for (let j = 1; j < cmd.length; j += 2) {
            const [x, y] = mapCoord(cmd[j], cmd[j + 1]);
            out.push(x, y);
        }
        return out;
    });
}

/**
 * Uniformly multiplies every coordinate of an absolute, simplified path (M/L/C/Q/Z only) by
 * `scale`. Used to turn a size-independent "unit path" (see normalizePathToBounds(path, 1, 1))
 * back into local pixel coordinates for the object's CURRENT width/height, every render - this
 * is what makes the path automatically follow the BoundedText when it's resized, instead of
 * being frozen at whatever size it had when the path was first set.
 * @param {Array} parsedPath absolute, simplified path commands
 * @param {number} scale
 * @returns {Array} a new path command array
 */
function scalePath(parsedPath, scale) {
    return parsedPath.map((cmd) => {
        const out = [cmd[0]];
        for (let j = 1; j < cmd.length; j++) {
            out.push(cmd[j] * scale);
        }
        return out;
    });
}

/**
 * Strokes a fabric.util.parsePath()/makePathSimpler() command array directly on the canvas,
 * plus a small dot marking the path's starting point (M command) - useful to see exactly
 * where the path sits relative to the text object while tuning `textPath` by hand.
 *
 * This draws with plain ctx.lineWidth/arc radius INSIDE the object's own cached render pass -
 * unlike fabric.Control rendering (which fabric always resets to just retina scaling before
 * calling render(), so control handles stay a constant screen size for free), this drawing IS
 * subject to the canvas's own viewport zoom: a 1000mm page needs a much smaller zoom than a
 * 100mm one to fit on screen, so a fixed line width here ends up proportionally thinner on
 * screen as the page gets bigger. `scale` compensates for that.
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array} parsedPath absolute, simplified path commands (M/L/C/Q/Z only)
 * @param {number} [scale] pass `1 / canvas.getZoom()` so the stroke/dot stay a constant on-screen
 *   size regardless of how zoomed out the canvas currently is
 * @param {string} [strokeColor]
 * @param {string} [startPointColor]
 */
function debugDrawPath(ctx, parsedPath, scale = 1, strokeColor = '#5181FF', startPointColor = '#5181FF') {
    const lineWidth = 3 * scale;
    const dotRadius = 5 * scale;
    const dash = [6 * scale, 4 * scale];

    ctx.save();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = lineWidth;
    ctx.setLineDash(dash);
    ctx.beginPath();
    for (let i = 0; i < parsedPath.length; i++) {
        const cmd = parsedPath[i];
        if (cmd[0] === 'M') {
            ctx.moveTo(cmd[1], cmd[2]);
        } else if (cmd[0] === 'L') {
            ctx.lineTo(cmd[1], cmd[2]);
        } else if (cmd[0] === 'C') {
            ctx.bezierCurveTo(cmd[1], cmd[2], cmd[3], cmd[4], cmd[5], cmd[6]);
        } else if (cmd[0] === 'Q') {
            ctx.quadraticCurveTo(cmd[1], cmd[2], cmd[3], cmd[4]);
        } else if (cmd[0] === 'Z') {
            ctx.closePath();
        }
    }
    ctx.stroke();
    ctx.restore();

    const start = parsedPath.find((cmd) => cmd[0] === 'M');
    if (start) {
        ctx.save();
        ctx.fillStyle = startPointColor;
        ctx.beginPath();
        ctx.arc(start[1], start[2], dotRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }
}

/**
 * Rigidly translates every coordinate in an absolute, simplified path (M/L/C/Q/Z only) straight
 * down by `offsetY` - the path-command-array equivalent of offsetPointOnPath(), used only to
 * visualize (via debugDrawPath) where each line's text actually sits when debugPath is on.
 * @param {Array} parsedPath absolute, simplified path commands
 * @param {number} offsetY
 * @returns {Array} a new path command array
 */
function translatePathCommands(parsedPath, offsetY) {
    if (!offsetY) {
        return parsedPath;
    }
    return parsedPath.map((cmd) => {
        const out = [cmd[0]];
        for (let j = 1; j < cmd.length; j += 2) {
            out.push(cmd[j], cmd[j + 1] + offsetY);
        }
        return out;
    });
}

/**
 * Shifts a point-on-path by `offset` along a FIXED direction (straight down the object's own
 * local Y axis) - this is what makes multi-line "Draw on Path" text possible: every line is the
 * same curve shape as the base path, rigidly translated, rather than a per-point perpendicular
 * offset that follows each point's own local normal.
 *
 * A per-point normal offset was tried first, but it distorts the curve: the further a point's
 * normal direction differs from its neighbors' (ie. the tighter the curve), the more offset
 * points at a large-enough offset converge toward each other instead of preserving their
 * original spacing - on a curve tight enough relative to the offset, they collapse into a
 * tangle. A rigid translation can never do that: every point moves by the exact same vector, so
 * distances between points (and therefore the curve's shape and character spacing) are
 * preserved exactly, no matter how tightly the base path curves or how large lineSpacing is.
 * @param {{x: number, y: number, angle: number}} pointInfo result of fabric.util.getPointOnPath()
 * @param {number} offset distance to shift straight down (object-local Y)
 * @returns {{x: number, y: number, angle: number}}
 */
function offsetPointOnPath(pointInfo, offset) {
    if (!offset) {
        return pointInfo;
    }
    return {
        x: pointInfo.x,
        y: pointInfo.y + offset,
        angle: pointInfo.angle
    };
}

/**
 * Draws `text` following `textPath`, one glyph at a time, rotated to match the
 * path tangent at that glyph's center position.
 *
 * @param {Object} options
 * @param {CanvasRenderingContext2D} options.ctx canvas context (already positioned/scaled by the caller)
 * @param {opentype.Font} options.font resolved opentype font (see window.Font.resolve())
 * @param {string} options.text text to render
 * @param {number} options.fontSize font size in px. With autoFit on, this is only a measurement
 *   seed (see the autoFit formula below) - the actual rendered size is derived from the path's
 *   length instead. With autoFit off, this IS the rendered size as-is (the caller is expected to
 *   have already clamped it - see OpentypeIText._renderTextAlongPath, which passes maxFontSize
 *   directly rather than fitting against the object's own box: the path can be much shorter than
 *   the object's box, so box-fit sizing was tried and reverted - it has no awareness of the path
 *   at all, and for very short text produces a size several times bigger than the path itself)
 * @param {string} options.textPath SVG path 'd' attribute the text should follow
 * @param {string|fabric.Gradient|fabric.Palette} [options.fill] plain color string, a
 *   fabric.Gradient (mapped onto the actual ink bounding box of the rendered path text), or a
 *   multi-color fabric.Palette (each glyph gets the next color in the palette, cycling)
 * @param {number} [options.startOffset] distance (in path units) to shift the start of the text
 * @param {boolean} [options.autoFit] if true, rescales the font size so the full text spans
 *   exactly the path length (grows or shrinks). If false, `fontSize` is used as-is (see its own
 *   comment above); text that doesn't fit within the path at that size simply has its
 *   overflowing characters skipped (see isWithinPath below) rather than shrinking further
 * @param {number} [options.minFontSize] lower clamp for the effective font size (mirrors BoundedText's minSizePx) -
 *   only meaningful with autoFit on, since without it `fontSize` is expected to already be
 *   pre-clamped by the caller
 * @param {number} [options.maxFontSize] upper clamp for the effective font size (mirrors BoundedText's maxSizePx) -
 *   only meaningful with autoFit on, for the same reason as minFontSize above
 * @param {boolean} [options.debugPath] if true, strokes the path in red with a green dot at its start point
 * @param {number} [options.pathScale] `textPath` is expected to already be a size-independent
 *   "unit path" (its own bounding box fits inside a 1x1 box - see setTextPath()/normalizePathToBounds),
 *   and gets uniformly multiplied by this factor to turn it into local pixel coordinates matching
 *   the BoundedText's CURRENT width/height. Passing the object's live size here (instead of baking
 *   a fixed pixel size into the stored path) is what makes the path follow the object on resize.
 * @param {string} [options.textAlign] 'left'/'center'/'right' - where the text starts along the
 *   path. Only has a visible effect when autoFit is false (with autoFit, the text always spans
 *   the full path length regardless of textAlign, same as fabric.Text's own path+textAlign behavior)
 * @param {number} [options.outlineWidth] stroke width (mirrors BoundedText's opentypeStrokeWidth).
 *   Drawn UNDER the fill on the same glyph paths (stroke then fill, same order
 *   window._fillTextOpentype uses), so only the part of the stroke sticking out past the fill
 *   edge is visible, instead of a stroke overlapping/washing out the fill color.
 * @param {string} [options.outlineColor] stroke color (mirrors BoundedText's opentypeStroke)
 * @param {number} [options.lineSpacing] text-on-path is naturally single-line (it follows one
 *   curve); a "\n" in `text` starts a new line, which follows the exact same curve shape as the
 *   base path, rigidly translated down by lineSpacing * effectiveFontSize (see
 *   offsetPointOnPath()) - same as normal multiline text uses lineSpacing for the vertical gap
 *   between rows
 * @param {{color?: string, blur?: number, offsetX?: number, offsetY?: number}} [options.shadow]
 *   drop shadow description (mirrors BoundedText's shadowType/shadowFill/shadowOffset/shadowBlur,
 *   see OpentypeIText.calculateShadowFill()); omitted/falsy means no shadow. Applied per line
 *   (and per glyph when using a multi-color palette fill), same fill/stroke/shadow draw order
 *   `window._fillTextOpentype`/drawPaths() use for normal (non-path) text
 * @param {number} [options.tracking] letter-spacing amount, in the same units BoundedText's
 *   `tracking` property already uses elsewhere (ems * 1000 - see OpentypeIText.js's own comment
 *   on that property) - passed straight through to opentype.js's `forEachGlyph`, which natively
 *   understands `options.tracking` (adds `tracking/1000 * fontSize` to the pen position after
 *   each glyph). Only needs to be added to the measurement glyphOptions below (naturalTextLength/
 *   masterPenPositions/penPositions all go through measurePenPositions, which shares that same
 *   options object) - font.getPath() itself draws one glyph in isolation, so tracking has nothing
 *   to affect there.
 * @returns {{ totalTextLength: number, effectiveFontSize: number, pathLength: number }} measurement info, useful to validate auto-fit math
 */
function drawTextAlongPath({
    ctx,
    font,
    text,
    fontSize,
    textPath,
    fill = '#000000',
    debugPath = false,
    // The debug stroke/dot are drawn with plain ctx.lineWidth/arc radius INSIDE the object's own
    //  cached render pass, which (unlike fabric.Control rendering, which fabric always resets to
    //  just retina scaling) is subject to the canvas's own viewport zoom - a 1000mm page needs a
    //  much smaller zoom than a 100mm one to fit on screen, so a fixed local-unit line width ends
    //  up proportionally thinner on screen. Callers pass 1/canvas.getZoom() here so the debug
    //  visuals stay a constant on-screen size regardless of how zoomed out the page is.
    debugScale = 1,
    startOffset = 0,
    autoFit = false,
    minFontSize,
    maxFontSize,
    pathScale = 1,
    textAlign = 'center',
    outlineWidth = 0,
    outlineColor = '#000000',
    lineSpacing = 1,
    tracking = 0,
    shadow = null
}) {
    const glyphOptions = {
        drawSVG: false,
        drawLayers: false,
        kerning: true,
        tracking,
        features: { liga: true, rlig: true }
    };

    // makePathSimpler() converts relative commands to absolute and reduces H/V/S/T/A/etc.
    //  down to plain M/L/C/Q/Z, which is the only vocabulary getPathSegmentsInfo() understands.
    //  Skipping this step (ie. feeding parsePath()'s raw output straight to getPathSegmentsInfo)
    //  silently mishandles anything but absolute M/L/C/Q paths - this is the same normalization
    //  fabric.Path itself applies to `this.path` before fabric.Text's native path feature uses it.
    const basePath = scalePath(parsePathToCommands(textPath), pathScale);
    const baseSegmentsInfo = fabric.util.getPathSegmentsInfo(basePath);
    const basePathLength = baseSegmentsInfo[baseSegmentsInfo.length - 1].length;

    if (debugPath) {
        debugDrawPath(ctx, basePath, debugScale);
    }

    // Measures the pen x-position of each glyph of `measureText` at the given font size (same
    //  pen-tracking approach used by window._measureTextOpentype / window._fillTextOpentype
    //  elsewhere in this codebase). Returns positions[i] = start x of character i, plus one
    //  extra trailing entry with the end-of-text position (measured by appending a space and
    //  reading its position, same trick used to size the last character's advance width like
    //  every other character's).
    const measurePenPositions = (measureText, measureFontSize) => {
        const positions = [];
        font.forEachGlyph(measureText, 0, 0, measureFontSize, glyphOptions, function (glyph, glyphX) {
            positions.push(glyphX);
        });
        let endPosition = 0;
        font.forEachGlyph(measureText + ' ', 0, 0, measureFontSize, glyphOptions, function (glyph, glyphX) {
            endPosition = glyphX;
        });
        positions.push(endPosition);
        return positions;
    };

    // Text-on-path is naturally single-line; "\n" splits it into several lines, each later drawn
    //  with its glyphs shifted off the base path (see offsetPointOnPath() and the loop below).
    const lines = text.split('\n');

    // All lines share ONE font size - autoFit sizes against whichever line is naturally the
    //  longest (the hardest one to fit), same as normal multiline text fits its font size
    //  against the widest line. Letting each line stretch independently would give every line a
    //  different type size, which reads as broken rather than as a paragraph.
    const naturalLineLengths = lines.map((lineText) => {
        const positions = measurePenPositions(lineText, fontSize);
        return positions[positions.length - 1];
    });
    const naturalTextLength = Math.max(0, ...naturalLineLengths);
    const longestLineIndex = naturalLineLengths.indexOf(naturalTextLength);

    // With autoFit, shrink/grow the actual font size (not just the spacing between glyphs) so
    //  the text visually fills the path without letters overlapping (when shrinking) or leaving
    //  gaps sized like the original font (when growing). Without autoFit, `fontSize` starts at
    //  maxFontSize (see its own comment above) and is only ever shrunk - never grown past that -
    //  when the text doesn't naturally fit the path at that size, same formula as autoFit but
    //  only entering this branch on overflow: unlike autoFit, short text stays at maxFontSize
    //  instead of stretching to fill unused path length. Shrinking is bounded below by
    //  minFontSize (see the clamp below); if it still doesn't fit even at minFontSize, whatever's
    //  left over simply isn't drawn (see isWithinPath below) rather than shrinking further.
    let effectiveFontSize = fontSize;
    if (naturalTextLength > 0 && (autoFit || naturalTextLength > basePathLength)) {
        effectiveFontSize = fontSize * (basePathLength / naturalTextLength);
    }
    // Clamp against the object's min/max font size the same way the normal (non-path) render
    //  does via forceFontSize()/minFontSize/maxFontSize - without this, a short text on a long
    //  path (or vice versa) can blow way past what BoundedText's minSizePx/maxSizePx allow.
    if (typeof minFontSize === 'number') {
        effectiveFontSize = Math.max(effectiveFontSize, minFontSize);
    }
    if (typeof maxFontSize === 'number') {
        effectiveFontSize = Math.min(effectiveFontSize, maxFontSize);
    }

    // `shadow`'s offset/blur (see OpentypeIText.calculateShadowFill()) are computed proportional
    //  to `fontSize`, the seed value passed in above - but path-mode's autoFit usually rescales
    //  the ACTUAL rendered size (effectiveFontSize) to something very different from that seed
    //  (eg. a small seed stretched to fill a long path), so a shadow sized for the seed ends up
    //  barely offset relative to the much bigger rendered glyphs, landing on top of the text
    //  instead of behind it. Rescale it by the same ratio autoFit already applied to the glyphs.
    const shadowScale = fontSize > 0 ? effectiveFontSize / fontSize : 1;
    const scaledShadow = shadow
        ? {
              color: shadow.color,
              offsetX: (shadow.offsetX || 0) * shadowScale,
              offsetY: (shadow.offsetY || 0) * shadowScale,
              blur: (shadow.blur || 0) * shadowScale
          }
        : null;

    const lineHeight = effectiveFontSize * (lineSpacing || 1);

    // Shared spacing template: character index i of EVERY line is placed at the arc-length
    //  position dictated by the LONGEST line's own advance widths, instead of each line
    //  accumulating its own characters' advance widths independently. Different characters
    //  (eg. a narrow "1" vs a wide "4") have different glyph widths even at the same font size,
    //  so without this, the same column index would land at a different point along the path on
    //  each line and the lines would look skewed/misaligned relative to each other even when
    //  they have the same number of characters. The rendered glyph itself is still that line's
    //  own actual character (own shape/width for rotation-centering) - only the STEPPING between
    //  columns is shared.
    const masterPenPositions = measurePenPositions(lines[longestLineIndex] || '', effectiveFontSize);

    ctx.save();
    if (outlineWidth > 0) {
        ctx.lineWidth = outlineWidth;
        ctx.strokeStyle = outlineColor;
        ctx.lineJoin = 'round';
    }

    lines.forEach((lineText, lineIndex) => {
        // Centered around the base path: line 0 goes up (negative offset, "canvas up") for
        //  multi-line text, later lines go down - same convention normal text uses top-to-bottom.
        const lineOffset = (lineIndex - (lines.length - 1) / 2) * lineHeight;

        if (debugPath) {
            // Every line's guide is drawn (including lineOffset === 0, if any line lands
            //  exactly on the base path) so it's obvious which dashed line belongs to which
            //  text row.
            debugDrawPath(ctx, translatePathCommands(basePath, lineOffset), debugScale, '#5181FF', '#5181FF');
        }

        // This line's OWN advance widths - only used below for centering each glyph's own shape
        //  on its anchor point (glyphs keep their natural per-character width); the STEPPING
        //  between columns instead comes from masterPenPositions (see the comment above it).
        const penPositions = measurePenPositions(lineText, effectiveFontSize);
        // How far this line's characters span using the SHARED template, ie. what "this line's
        //  rendered length" means when every column's spacing comes from the longest line.
        const renderedTextLength = masterPenPositions[lineText.length] - masterPenPositions[0];

        let positionInLine = startOffset;
        const leftover = basePathLength - renderedTextLength;
        if (textAlign === 'center') {
            positionInLine += leftover / 2;
        } else if (textAlign === 'right') {
            positionInLine += leftover;
        }

        // Multi-color palettes need each glyph filled separately (cycling through the palette's
        //  colors, same as drawPaths() in utils.js does for normal text) instead of one combined
        //  fill - a single fillStyle can't paint different colors per glyph in one fill() call.
        //  Restarts at the palette's first color on every line.
        const useMultiColorPalette = isMultiColorPalette(fill);
        const useGradient = !useMultiColorPalette && isFabricGradient(fill);
        // Ink bounding box of this line's glyphs, only tracked for the gradient case - needed to
        //  map the gradient's normalized [0,1] space onto where the line's text actually landed
        //  (same idea as OpentypeIText._updateGradientFill, which uses the object's width/height
        //  instead since normal text is a simple rectangle - path text isn't, so the actual ink
        //  extent is used; each line gets its own gradient extent rather than sharing one).
        const bbox = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
        let paletteIndex = 0;

        if (!useMultiColorPalette) {
            ctx.beginPath();
        }

        for (let i = 0; i < lineText.length; i++) {
            const character = lineText[i];
            // Own glyph's width (for centering its shape on its anchor point)...
            const advanceWidth = penPositions[i + 1] - penPositions[i];
            // ...vs. the shared template's step for this column (for where that anchor point
            //  actually falls along the path) - see the masterPenPositions comment above.
            const stepWidth = masterPenPositions[i + 1] - masterPenPositions[i];
            const centerPositionInLine = positionInLine + stepWidth / 2;

            // Skip characters whose anchor point would fall outside the path's own length -
            //  this can happen even with autoFit on, since effectiveFontSize gets clamped
            //  against minFontSize/maxFontSize AFTER autoFit computes the "true" fit size (eg. a
            //  strict minFontSize can force the font bigger than what actually fits a long text
            //  on this path). Rather than wrapping the excess back onto the path (which drops
            //  characters on top of already-placed ones, visibly overlapping) or extrapolating
            //  them off the end in a straight line, they're simply not drawn.
            const isWithinPath = centerPositionInLine >= 0 && centerPositionInLine <= basePathLength;
            if (character.trim() !== '' && isWithinPath) {
                const basePointInfo = fabric.util.getPointOnPath(basePath, centerPositionInLine, baseSegmentsInfo);
                const pointInfo = offsetPointOnPath(basePointInfo, lineOffset);

                const glyphPath = font.getPath(character, 0, 0, effectiveFontSize, glyphOptions);
                const transformed = transformGlyphCommands(
                    glyphPath.commands,
                    advanceWidth / 2,
                    pointInfo.angle,
                    pointInfo.x,
                    pointInfo.y
                );

                if (useMultiColorPalette) {
                    ctx.beginPath();
                    addCommandsToCtx(ctx, transformed);
                    // Stroke UNDER the fill (same order window._fillTextOpentype uses) so the
                    //  outline is only visible where it sticks out past the fill edge
                    const glyphColor = fill.getColor(paletteIndex++);
                    if (scaledShadow) {
                        // Cast the shadow together with the stroke (+ a throwaway fill, so the
                        //  shadow has an opaque shape to follow) in an isolated save/restore -
                        //  the crisp, un-shadowed stroke+fill actually meant to be seen are
                        //  drawn after restore(), same two-pass approach used below/in utils.js.
                        ctx.save();
                        applyShadowToCtx(ctx, scaledShadow);
                        ctx.fillStyle = glyphColor;
                        if (outlineWidth > 0) {
                            ctx.stroke();
                        }
                        ctx.fill();
                        ctx.restore();
                    }
                    if (outlineWidth > 0) {
                        // Drawn again here (crisp, without the shadow) even when scaledShadow
                        //  already stroked once above - otherwise the visible outline itself
                        //  would carry the shadow's blur.
                        ctx.stroke();
                    }
                    ctx.fillStyle = glyphColor;
                    ctx.fill();
                } else {
                    addCommandsToCtx(ctx, transformed);
                    if (useGradient) {
                        expandBBoxWithCommands(transformed, bbox);
                    }
                }
            }

            positionInLine += stepWidth;
        }

        if (!useMultiColorPalette) {
            if (scaledShadow && outlineWidth === 0) {
                // No stroke: draw shadow + fill together in one step, using the real fill style
                //  (transparency-safe - same reasoning as window._fillTextOpentype's `_fillShadow
                //  != null && opentypeStrokeWidth === 0` branch in OpentypeIText.js).
                ctx.save();
                applyShadowToCtx(ctx, scaledShadow);
                if (useGradient) {
                    const bboxWidth = bbox.maxX - bbox.minX || 1;
                    const bboxHeight = bbox.maxY - bbox.minY || 1;
                    ctx.fillStyle = fill.toLive(ctx);
                    ctx.transform(bboxWidth, 0, 0, bboxHeight, bbox.minX, bbox.minY);
                    if (fill.gradientTransform) {
                        const m = fill.gradientTransform;
                        ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5]);
                    }
                } else {
                    ctx.fillStyle = typeof fill === 'object' && fill && fill.toLive ? fill.toLive(ctx) : fill;
                }
                ctx.fill();
                ctx.restore();
                return;
            }

            if (scaledShadow) {
                // Cast the shadow together with the stroke (+ a throwaway fill, so the shadow
                //  has an opaque shape to follow) in an isolated save/restore, same two-pass
                //  approach window._fillTextOpentype/drawPaths() in utils.js use: the shadowed
                //  stroke+fill here are throwaway (covered up next), the crisp, un-shadowed
                //  stroke+fill that are actually meant to be seen are drawn after restore().
                //  fillStyle must be set here (unlike the normal-text version, which already has
                //  it set globally before drawPaths() runs) - otherwise this fill() paints with
                //  whatever fillStyle was last left on the shared ctx and the shadow ends up only
                //  following the stroke's ring instead of the whole glyph shape.
                ctx.save();
                applyShadowToCtx(ctx, scaledShadow);
                ctx.fillStyle = typeof fill === 'object' && fill && fill.toLive ? fill.toLive(ctx) : fill;
                ctx.stroke();
                ctx.fill();
                ctx.restore();
            }
            if (outlineWidth > 0) {
                // Stroke UNDER the fill (same order window._fillTextOpentype uses) so the
                //  outline is only visible where it sticks out past the fill edge. Drawn again
                //  here (crisp, without the shadow) even when scaledShadow already stroked once
                //  above - otherwise the visible outline itself would carry the shadow's blur.
                ctx.stroke();
            }
            if (useGradient) {
                const bboxWidth = bbox.maxX - bbox.minX || 1;
                const bboxHeight = bbox.maxY - bbox.minY || 1;
                ctx.save();
                ctx.fillStyle = fill.toLive(ctx);
                // Maps the gradient's own normalized [0,1] space onto the actual ink bounding
                //  box, same transform-after-path-is-built trick used everywhere else in this
                //  codebase for gradients/patterns (the CTM at fill() time controls where the
                //  gradient paints, independent of the coordinate space the path itself was
                //  already recorded in).
                ctx.transform(bboxWidth, 0, 0, bboxHeight, bbox.minX, bbox.minY);
                if (fill.gradientTransform) {
                    const m = fill.gradientTransform;
                    ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5]);
                }
                ctx.fill();
                ctx.restore();
            } else {
                ctx.fillStyle = typeof fill === 'object' && fill && fill.toLive ? fill.toLive(ctx) : fill;
                ctx.fill();
            }
        }
    });

    ctx.restore();

    return { totalTextLength: naturalTextLength, effectiveFontSize, pathLength: basePathLength };
}

/**
 * Parses an SVG 'd' string into fabric's absolute, simplified command array (M/L/C/Q/Z only).
 * See the comment above where this same expression is used inside drawTextAlongPath for why
 * makePathSimpler() is required (getPathSegmentsInfo/getPointOnPath only understand that vocabulary).
 * @param {string} pathData
 * @returns {Array}
 */
function parsePathToCommands(pathData) {
    return fabric.util.makePathSimpler(fabric.util.parsePath(pathData));
}

module.exports = { drawTextAlongPath, normalizePathToBounds, parsePathToCommands, scalePath };
